import { isGoogleEmulatorKind } from "./google-emulator.js";
import { isSharedMailboxInstance } from "./shared-mailbox.js";
import { z } from "zod";
import { channelDataFreshnessSchema } from "./channel-data-trace.js";
import { addConnectorConfigValidationIssues, parseConnectorConfig, } from "./connector-configs.js";
export const connectorProviderSchema = z.enum([
    "xero",
    "sun",
    "overture",
    "stripe",
    "gocardless",
    "sharepoint",
    "gmail",
    "outlook_mail",
    "google_calendar",
    "google_drive",
    "google_sheets",
    "github",
    "granola",
    "slack",
    "hubspot",
    /** The Twin itself as a source: confirmed identity links from Twin Matching. */
    "twin",
]);
export const connectorInstanceKindSchema = z.enum(["repositories", "notifications", "emulator", "shared_mailbox"]);
export const USER_SCOPED_CONNECTOR_PROVIDERS = [
    "gmail",
    "outlook_mail",
    "google_calendar",
    "google_drive",
    "google_sheets",
    "granola",
    "slack",
];
export const GITHUB_CONNECTOR_ENTITIES = [
    "repository",
    "branch",
    "commit",
    "pull_request",
];
export const HUBSPOT_CONNECTOR_ENTITIES = [
    "contacts",
    "companies",
    "deals",
    "owners",
    "pipelines",
    "engagements",
];
export function isUserScopedConnectorProvider(provider) {
    return USER_SCOPED_CONNECTOR_PROVIDERS.includes(provider);
}
export function isUserScopedConnectorInstance(provider, connectorKind) {
    return !isGoogleEmulatorKind(provider, connectorKind)
        && !isSharedMailboxInstance(provider, connectorKind)
        && (isUserScopedConnectorProvider(provider)
            || (provider === "github" && connectorKind === "notifications"));
}
export const XERO_CONNECTOR_ENTITIES = [
    "accounts",
    "contacts",
    "invoices",
    "credit_notes",
    "payments",
    "purchase_orders",
    "repeating_invoices",
    "quotes",
    "bank_transactions",
    "tracking_categories",
    "tax_rates",
    "journals",
    "attachments",
    "contact_history",
];
export function canonicalXeroConnectorEntity(entity) {
    const normalized = entity.trim().toLowerCase().replaceAll("-", "_");
    if (normalized === "tracking")
        return "tracking_categories";
    if (normalized === "expenses")
        return "invoices";
    if (normalized === "creditnotes")
        return "credit_notes";
    if (normalized === "purchaseorders")
        return "purchase_orders";
    if (normalized === "repeatinginvoices")
        return "repeating_invoices";
    if (normalized === "history" || normalized === "contacthistory") {
        return "contact_history";
    }
    if (XERO_CONNECTOR_ENTITIES.includes(normalized)) {
        return normalized;
    }
    throw new Error(`Unsupported Xero entity '${entity}'`);
}
export function selectXeroConnectorEntities(input) {
    if (input.requestedEntity) {
        return [canonicalXeroConnectorEntity(input.requestedEntity)];
    }
    // Xero connections are organisation-wide accounting sources. Every enabled
    // connection ingests the complete provider contract; entity scoping is only
    // supported for an explicit one-off operational job.
    return [...XERO_CONNECTOR_ENTITIES];
}
export function connectorEnabledEntities(input) {
    return input.provider === "xero"
        ? [...XERO_CONNECTOR_ENTITIES]
        : [...(input.requestedEntities ?? [])];
}
export const SLACK_CONNECTION_CAPABILITIES = [
    "runtime",
    "messaging",
    "source_sync",
];
export const slackConnectionCapabilitySchema = z.enum(SLACK_CONNECTION_CAPABILITIES);
export function parseSlackConnectionCapabilities(value) {
    const parsed = z
        .array(slackConnectionCapabilitySchema)
        .min(1)
        .safeParse((typeof value === "string" ? value.split(",") : value)
        .map((capability) => capability.trim())
        .filter(Boolean));
    if (!parsed.success) {
        throw new Error("Slack OAuth requires one or more valid connection capabilities.");
    }
    return [...new Set(parsed.data)];
}
/** Resolve the Slack source capabilities that the granted token can ingest. */
export function slackConnectorScopeCapabilities(grantedScope) {
    const scopes = new Set((typeof grantedScope === "string" ? grantedScope.split(/[,\s]+/u) : grantedScope)
        .map((scope) => scope.trim()).filter(Boolean));
    const hasAll = (...required) => required.every((scope) => scopes.has(scope));
    return {
        mentionConversationTypes: [
            ...(hasAll("channels:read", "channels:history") ? ["public_channel"] : []),
            ...(hasAll("groups:read", "groups:history") ? ["private_channel"] : []),
        ],
        directMessageConversationTypes: [
            ...(hasAll("im:read", "im:history") ? ["im"] : []),
            ...(hasAll("mpim:read", "mpim:history") ? ["mpim"] : []),
        ],
        canReadUsers: scopes.has("users:read"),
    };
}
export function slackConnectorEntitiesForGrantedScopes(grantedScope) {
    const capabilities = slackConnectorScopeCapabilities(grantedScope);
    return [
        ...(capabilities.mentionConversationTypes.length > 0 ? ["mention"] : []),
        ...(capabilities.directMessageConversationTypes.length > 0 ? ["direct_message"] : []),
    ];
}
/** Bot scopes required by the shared Slack app for outbound notifications and inbound Twin conversations. */
export const SLACK_CONVERSATION_BOT_SCOPES = [
    "app_mentions:read",
    "channels:history",
    "channels:read",
    "chat:write",
    "groups:history",
    "groups:read",
    "im:history",
    "im:read",
    "mpim:history",
    "mpim:read",
    "reactions:write",
    "users:read",
];
/** User scopes required for on-demand Slack queries and the optional source connector. */
export const SLACK_USER_SCOPES = [
    "search:read.public",
    "search:read.private",
    "search:read.mpim",
    "search:read.im",
    "search:read.files",
    "search:read.users",
    "chat:write",
    "channels:read",
    "channels:history",
    "channels:write",
    "groups:read",
    "groups:history",
    "groups:write",
    "mpim:read",
    "mpim:history",
    "mpim:write",
    "im:read",
    "im:history",
    "im:write",
    "canvases:read",
    "canvases:write",
    "emoji:read",
    "files:read",
    "reactions:write",
    "users:read",
    "users:read.email",
];
// Canonical entity sets enabled for a connector integration created without an
// explicit selection (the native OAuth handoff carries no entity picker). Kept
// in contracts because the control-plane provisions integrations while the
// connector-runner owns each provider's entity list — the runner asserts these
// stay in step with its own sets (twin-integrations/connector-runner/src/default-entities.test.ts).
export const CONNECTOR_DEFAULT_ENABLED_ENTITIES = {
    gmail: ["message"],
    outlook_mail: ["message"],
    google_calendar: ["event"],
    google_drive: ["file"],
    github: GITHUB_CONNECTOR_ENTITIES,
    granola: ["note"],
    xero: XERO_CONNECTOR_ENTITIES,
    stripe: [
        "balance_transactions",
        "charges",
        "checkout_sessions",
        "credit_notes",
        "customers",
        "disputes",
        "events",
        "invoice_items",
        "invoices",
        "payment_intents",
        "payouts",
        "prices",
        "products",
        "refunds",
        "subscriptions",
    ],
    sharepoint: ["documents"],
    slack: ["mention", "direct_message"],
    hubspot: HUBSPOT_CONNECTOR_ENTITIES,
    twin: ["identity_link"],
};
export function defaultEnabledEntitiesForProvider(provider) {
    const defaults = CONNECTOR_DEFAULT_ENABLED_ENTITIES[provider];
    return defaults ? [...defaults] : [];
}
export const connectorStatusSchema = z.enum([
    "configured",
    "disabled",
    "error",
    "syncing",
]);
export const connectorJobStatusSchema = z.enum([
    "queued",
    "running",
    "succeeded",
    "failed",
    "cancelled",
]);
export const connectorJobModeSchema = z.enum(["delta", "full"]);
export const connectorDeltaStrategySchema = z.enum([
    "exact",
    "rolling_window",
    "full_only",
]);
export const connectorWatermarkStrategySchema = z.enum([
    "provider_updated_at",
    "observed_through",
    "ingestion_completed_at",
    "unavailable",
]);
export const connectorProviderCapabilitiesSchema = z.object({
    deltaStrategy: connectorDeltaStrategySchema,
    watermarkStrategy: connectorWatermarkStrategySchema,
    defaultMode: connectorJobModeSchema,
    maxParallelEntities: z.number().int().positive(),
    webhookWakeups: z.boolean(),
});
export const connectorSchedulePolicySchema = z
    .object({
    enabled: z.boolean(),
    mode: connectorJobModeSchema,
    intervalMs: z.number().int().positive(),
    maxStalenessMs: z.number().int().positive(),
    reconciliationIntervalMs: z.number().int().positive().optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.intervalMs >= value.maxStalenessMs) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "intervalMs must be less than maxStalenessMs",
            path: ["intervalMs"],
        });
    }
    if (value.reconciliationIntervalMs !== undefined &&
        value.reconciliationIntervalMs < value.intervalMs) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "reconciliationIntervalMs must not be shorter than intervalMs",
            path: ["reconciliationIntervalMs"],
        });
    }
});
export const connectorSyncTriggerSchema = z.enum([
    "scheduled",
    "manual",
    "connect",
    "webhook",
    "reconcile",
    "recovery",
]);
export const connectorSyncRunStatusSchema = z.enum([
    "requested",
    "planning",
    "queued",
    "running",
    "succeeded",
    "partial",
    "failed",
    "cancelled",
]);
export const connectorSyncWindowSchema = z
    .object({
    since: z
        .string()
        .trim()
        .min(1)
        .max(128)
        .refine((value) => Number.isFinite(Date.parse(value)), {
        message: "since must be a valid date/timestamp",
    })
        .optional(),
    lookbackDays: z.number().int().positive().max(3650).optional(),
    resumeFromJobId: z.string().trim().min(1).max(200).optional(),
    sourceIds: z.array(z.string().regex(/^[1-9][0-9]*$/u)).min(1).max(100).optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.resumeFromJobId && Object.keys(value).some((key) => key !== "resumeFromJobId")) {
        ctx.addIssue({ code: "custom", path: ["resumeFromJobId"], message: "Checkpoint resume cannot be combined with another sync window" });
    }
    if (value.sourceIds && (value.since !== undefined || value.lookbackDays !== undefined)) {
        ctx.addIssue({ code: "custom", path: ["sourceIds"], message: "Source IDs cannot be combined with a date window" });
    }
    if (value.sourceIds && new Set(value.sourceIds).size !== value.sourceIds.length) {
        ctx.addIssue({ code: "custom", path: ["sourceIds"], message: "Source IDs must be unique" });
    }
    if (value.since !== undefined && value.lookbackDays !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Use either since or lookbackDays, not both",
            path: ["lookbackDays"],
        });
    }
});
export const connectorEntityJobSpecSchema = z
    .object({
    entity: z.string().trim().min(1).max(128),
    mode: connectorJobModeSchema,
    window: connectorSyncWindowSchema.default({}),
})
    .superRefine((value, ctx) => {
    validateSyncWindowForMode(value.mode, value.window, ctx);
});
export const connectorIngestionOutcomeSchema = z.object({
    jobId: z.string().trim().min(1),
    jobEntity: z.string().trim().min(1).max(128).nullable(),
    entity: z.string().trim().min(1).max(128),
    mode: connectorJobModeSchema,
    startedAt: z.number().int().nonnegative(),
    finishedAt: z.number().int().nonnegative(),
    itemsProcessed: z.number().int().nonnegative(),
    rawEventsTotal: z.number().int().nonnegative(),
    rawEventsChanged: z.number().int().nonnegative(),
    checkpoint: z.record(z.string(), z.unknown()),
    deltaStrategy: connectorDeltaStrategySchema,
    watermarkStrategy: connectorWatermarkStrategySchema,
    sourceWatermark: z.number().int().nonnegative().optional(),
    observedThrough: z.number().int().nonnegative(),
}).superRefine((value, ctx) => {
    if (value.finishedAt < value.startedAt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "finishedAt must not precede startedAt",
            path: ["finishedAt"],
        });
    }
});
export const connectorIntegrationSchema = z.object({
    id: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    userId: z.string().trim().min(1).optional(),
    provider: connectorProviderSchema,
    status: connectorStatusSchema,
    enabled: z.boolean(),
    enabledEntities: z.array(z.string().trim().min(1)),
    config: z.record(z.string(), z.unknown()),
    secretProvider: z.enum(["infisical", "local-postgres"]),
    secretPath: z.string().trim().min(1).optional(),
    lastSyncStartedAt: z.number().optional(),
    lastSyncFinishedAt: z.number().optional(),
    lastSuccessfulSyncAt: z.number().optional(),
    lastError: z.string().optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const listConnectorIntegrationsResponseSchema = z.object({
    integrations: z.array(connectorIntegrationSchema),
});
/*
 * A Data Set fed by one connector entity: the channel object type whose
 * dataset depends on this integration and entity, how many records it holds,
 * and its current freshness. Knowledge uses it to open the Data Set an entity
 * feeds rather than only its raw events.
 */
export const connectorDataSetBindingSchema = z.object({
    integrationId: z.string().trim().min(1),
    provider: z.string().trim().min(1),
    entity: z.string().trim().min(1),
    channelId: z.string().trim().min(1),
    channelName: z.string().trim().min(1),
    objectTypeId: z.string().trim().min(1),
    objectTypeName: z.string().trim().min(1),
    objectTypeSlug: z.string().trim().min(1),
    recordCount: z.number().int().nonnegative(),
    freshness: channelDataFreshnessSchema.nullable(),
});
export const listConnectorDataSetsResponseSchema = z.object({
    dataSets: z.array(connectorDataSetBindingSchema),
});
export const connectorIntegrationLocatorSchema = z.object({
    organisationId: z.string().trim().min(1).max(256),
    provider: connectorProviderSchema,
    connectorKind: connectorInstanceKindSchema.optional(),
    /** Names one shared mailbox; an organisation can have several for one provider. */
    integrationId: z.string().trim().min(1).max(256).optional(),
}).strict();
/** A shared mailbox is addressed by its connection id; nothing else takes one. */
export function addSharedMailboxLocatorIssues(value, ctx) {
    const sharedMailbox = isSharedMailboxInstance(value.provider, value.connectorKind);
    if (sharedMailbox && !value.integrationId) {
        ctx.addIssue({ code: "custom", path: ["integrationId"],
            message: "integrationId is required for a shared mailbox" });
    }
    if (!sharedMailbox && value.integrationId) {
        ctx.addIssue({ code: "custom", path: ["integrationId"],
            message: "integrationId names a shared mailbox (Gmail with connectorKind shared_mailbox)" });
    }
}
/** One connection to remove: a locator whose shared mailbox, if any, is named by id. */
export const connectorIntegrationTargetSchema = connectorIntegrationLocatorSchema
    .superRefine((value, ctx) => addSharedMailboxLocatorIssues(value, ctx));
/** Shared mailboxes and the Google emulator are Gmail kinds; GitHub has its own. */
function isSupportedConnectorKind(provider, connectorKind) {
    return provider === "github" || isGoogleEmulatorKind(provider, connectorKind)
        || isSharedMailboxInstance(provider, connectorKind);
}
export const upsertConnectorIntegrationRequestSchema = connectorIntegrationLocatorSchema.extend({
    enabled: z.boolean().default(true),
    enabledEntities: z.array(z.string().trim().min(1).max(128)).default([]),
    config: z.record(z.string(), z.unknown()).default({}),
    secrets: z.record(z.string().trim().min(1).max(128), z.string().min(1).max(50_000)),
}).superRefine((value, ctx) => {
    addConnectorConfigValidationIssues({
        provider: value.provider,
        operation: "create",
        config: value.config,
        ctx,
        path: "config",
    });
    const configKind = value.config.connectorKind;
    if (value.connectorKind !== undefined
        && isSupportedConnectorKind(value.provider, value.connectorKind)
        && value.connectorKind !== configKind) {
        ctx.addIssue({
            code: "custom",
            path: ["connectorKind"],
            message: "connectorKind must match config.connectorKind",
        });
    }
    if (value.integrationId !== undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["integrationId"],
            message: "integrationId cannot be set on an upsert",
        });
    }
    if (value.connectorKind !== undefined && !isSupportedConnectorKind(value.provider, value.connectorKind)) {
        ctx.addIssue({
            code: "custom",
            path: ["connectorKind"],
            message: "connectorKind is supported only for GitHub connectors",
        });
    }
}).transform((value) => ({
    ...value,
    config: parseConnectorConfig(value.provider, "create", value.config),
    ...((value.provider === "github" || isGoogleEmulatorKind(value.provider, value.config.connectorKind))
        ? { connectorKind: connectorInstanceKindSchema.parse(value.config.connectorKind) }
        : {}),
}));
export const upsertConnectorIntegrationResponseSchema = connectorIntegrationSchema;
export const updateConnectorIntegrationConfigRequestSchema = connectorIntegrationLocatorSchema.extend({
    configPatch: z.record(z.string(), z.unknown()),
}).superRefine((value, ctx) => {
    addConnectorConfigValidationIssues({
        provider: value.provider,
        operation: "update",
        config: value.configPatch,
        ctx,
        path: "configPatch",
    });
    const patchKind = value.configPatch.connectorKind;
    if ((value.provider === "github" || isGoogleEmulatorKind(value.provider, value.connectorKind))
        && value.connectorKind !== undefined
        && value.connectorKind !== patchKind) {
        ctx.addIssue({
            code: "custom",
            path: ["connectorKind"],
            message: "connectorKind must match configPatch.connectorKind",
        });
    }
    if (value.integrationId !== undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["integrationId"],
            message: "a shared mailbox's settings cannot be changed",
        });
    }
    if (value.provider !== "github" && value.connectorKind !== undefined && !isGoogleEmulatorKind(value.provider, value.connectorKind)) {
        ctx.addIssue({
            code: "custom",
            path: ["connectorKind"],
            message: "connectorKind is supported only for GitHub connectors",
        });
    }
}).transform((value) => ({
    ...value,
    configPatch: parseConnectorConfig(value.provider, "update", value.configPatch),
    ...(value.provider === "github"
        ? { connectorKind: connectorInstanceKindSchema.parse(value.configPatch.connectorKind) }
        : {}),
}));
export const updateConnectorIntegrationConfigResponseSchema = connectorIntegrationSchema;
export const deleteConnectorIntegrationResponseSchema = z.object({
    ok: z.literal(true),
    organisationId: z.string().trim().min(1),
    provider: connectorProviderSchema,
});
function validateSyncWindowForMode(mode, window, ctx) {
    if (window.resumeFromJobId && mode !== "full") {
        ctx.addIssue({ code: "custom", path: ["window"], message: "Checkpoint resume requires full mode" });
    }
    if (mode === "full" &&
        Object.keys(window).some((key) => key !== "resumeFromJobId")) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "since/lookbackDays are not supported for full sync jobs",
            path: ["window"],
        });
    }
}
export const createConnectorSyncJobRequestSchema = z
    .object({
    organisationId: z.string().trim().min(1).max(256),
    provider: connectorProviderSchema,
    connectorKind: connectorInstanceKindSchema.optional(),
    /** Names one shared mailbox; see connectorIntegrationLocatorSchema. */
    integrationId: z.string().trim().min(1).max(256).optional(),
    entity: z.string().trim().min(1).max(128).optional(),
    mode: connectorJobModeSchema.default("delta"),
    window: connectorSyncWindowSchema.default({}),
})
    .superRefine((value, ctx) => {
    validateSyncWindowForMode(value.mode, value.window, ctx);
    if (value.connectorKind === "emulator" && !isGoogleEmulatorKind(value.provider, value.connectorKind))
        ctx.addIssue({ code: "custom", path: ["connectorKind"], message: "Emulator kind requires Gmail or Calendar" });
    if (value.window.resumeFromJobId && (value.provider !== "overture" ||
        !["calendar_events", "event_bookings", "booking-list-items"].includes(value.entity ?? ""))) {
        ctx.addIssue({ code: "custom", path: ["window", "resumeFromJobId"], message: "Checkpoint resume requires one Overture calendar_events, event_bookings or booking-list-items entity" });
    }
    if (value.window.sourceIds && (value.provider !== "overture" ||
        !["booking-list-items", "calendar_events"].includes(value.entity ?? ""))) {
        ctx.addIssue({ code: "custom", path: ["window", "sourceIds"],
            message: "Source ID recovery requires one Overture booking-list-items or calendar_events entity" });
    }
    if (value.provider === "github" && !value.connectorKind) {
        ctx.addIssue({
            code: "custom",
            path: ["connectorKind"],
            message: "connectorKind is required for GitHub sync jobs",
        });
    }
    if (value.connectorKind && !isSupportedConnectorKind(value.provider, value.connectorKind)) {
        ctx.addIssue({
            code: "custom",
            path: ["connectorKind"],
            message: "connectorKind is supported only for GitHub sync jobs",
        });
    }
    addSharedMailboxLocatorIssues(value, ctx);
});
export const connectorJobSchema = z
    .object({
    id: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    userId: z.string().trim().min(1).optional(),
    syncRunId: z.string().trim().min(1).optional(),
    provider: connectorProviderSchema,
    entity: z.string().trim().min(1).optional(),
    mode: connectorJobModeSchema,
    window: connectorSyncWindowSchema.default({}),
    status: connectorJobStatusSchema,
    attempts: z.number().int().nonnegative(),
    maxAttempts: z.number().int().positive(),
    error: z.string().optional(),
    claimedBy: z.string().optional(),
    claimedAt: z.number().optional(),
    startedAt: z.number().optional(),
    finishedAt: z.number().optional(),
    nextAttemptAt: z.number().optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
})
    .superRefine((value, ctx) => {
    validateSyncWindowForMode(value.mode, value.window, ctx);
});
export const connectorIntegrationScheduleSchema = z.object({
    integrationId: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    provider: connectorProviderSchema,
    userId: z.string().trim().min(1).optional(),
    policy: connectorSchedulePolicySchema,
    nextDueAt: z.number().int().nonnegative(),
    lastSchedulerAttemptAt: z.number().int().nonnegative().optional(),
    lastSchedulerSuccessAt: z.number().int().nonnegative().optional(),
    lastSchedulerFailureAt: z.number().int().nonnegative().optional(),
    lastSchedulerErrorCode: z.string().trim().min(1).optional(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
});
export const connectorSyncRunSchema = z.object({
    id: z.string().trim().min(1),
    integrationId: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    provider: connectorProviderSchema,
    userId: z.string().trim().min(1).optional(),
    trigger: connectorSyncTriggerSchema,
    mode: connectorJobModeSchema,
    requestedEntities: z.array(z.string().trim().min(1).max(128)),
    window: connectorSyncWindowSchema.default({}),
    status: connectorSyncRunStatusSchema,
    dueAt: z.number().int().nonnegative(),
    deadlineAt: z.number().int().nonnegative(),
    startedAt: z.number().int().nonnegative().optional(),
    finishedAt: z.number().int().nonnegative().optional(),
    completedSourceWatermark: z.number().int().nonnegative().optional(),
    terminalReasonCode: z.string().trim().min(1).optional(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
}).superRefine((value, ctx) => {
    validateSyncWindowForMode(value.mode, value.window, ctx);
    if (value.deadlineAt < value.dueAt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "deadlineAt must not be earlier than dueAt",
            path: ["deadlineAt"],
        });
    }
});
export const connectorSyncRunProgressSchema = z.object({
    total: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
    running: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
});
export const createConnectorSyncJobResponseSchema = z.object({
    run: connectorSyncRunSchema,
    progress: connectorSyncRunProgressSchema,
    // Compatibility field for clients that still render entity-job detail.
    // It can be empty while a newly requested parent run awaits planning.
    jobs: z.array(connectorJobSchema),
});
export const listConnectorJobsResponseSchema = z.object({
    jobs: z.array(connectorJobSchema),
});
export const cancelConnectorJobResponseSchema = z.object({
    job: connectorJobSchema,
});
export const deleteConnectorJobResponseSchema = z.object({
    deleted: z.boolean(),
});
export const connectorRawEventSummarySchema = z.object({
    organisationId: z.string().trim().min(1),
    provider: connectorProviderSchema,
    entity: z.string().trim().min(1),
    count: z.number().int().nonnegative(),
    maxOccurredAt: z.number().nullable(),
    maxCreatedAt: z.number().nullable(),
});
export const listConnectorRawEventSummariesResponseSchema = z.object({
    summaries: z.array(connectorRawEventSummarySchema),
});
export const connectorRawEventSchema = z.object({
    organisationId: z.string().trim().min(1),
    provider: connectorProviderSchema,
    entity: z.string().trim().min(1),
    externalId: z.string().trim().min(1),
    occurredAt: z.number().int().nonnegative(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
    tenantId: z.string().nullable(),
    userId: z.string().nullable(),
    r2Key: z.string().nullable(),
    payload: z.unknown(),
    /** 0..1 relevance when the list was a search; exact id matches score 1. */
    matchScore: z.number().min(0).max(1).optional(),
});
export const listConnectorRawEventsResponseSchema = z.object({
    events: z.array(connectorRawEventSchema),
    hasMore: z.boolean(),
    nextCursor: z.string().nullable(),
});
/*
 * Text extracted from a document the twin holds: a connector document (Reducto
 * extraction keyed by source, entity and external id) or an uploaded workspace
 * file (extracted the first time a run reads it). Text is capped server-side.
 */
export const documentExtractionSummarySchema = z.object({
    status: z.enum(["pending", "submitted", "completed", "skipped", "failed"]),
    filename: z.string().nullable(),
    contentType: z.string().nullable(),
    fileSizeBytes: z.number().int().nonnegative().nullable(),
    numPages: z.number().int().nonnegative().nullable(),
    extractedText: z.string().nullable(),
    textTruncated: z.boolean(),
    completedAt: z.number().int().nonnegative().nullable(),
    errorCode: z.string().nullable(),
    updatedAt: z.number().int().nonnegative(),
});
export const getDocumentExtractionResponseSchema = z.object({
    extraction: documentExtractionSummarySchema.nullable(),
});
/** The files attached to one synced record (a receipt on a Xero bill), each as its raw event holds it. */
export const listConnectorRecordDocumentsResponseSchema = z.object({
    documents: z.array(z.object({
        provider: connectorProviderSchema,
        entity: z.string(),
        externalId: z.string(),
        occurredAt: z.number(),
        payload: z.unknown(),
    })).max(20),
});
